import { combineLatest } from 'rxjs';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToString } from 'src/lib/fliko/helpers/toString.helper';
import { Nullable } from 'src/lib/fliko/types/typeish.type';
import { EstateService } from 'src/app/view/estate-view/services/estate.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ResidentService } from 'src/app/view/users/services/resident.service';
import { SelectInputHelper } from '../../helpers/select-input.helper';
import { SelectInputData } from '../../interfaces/select-input-data.interface';
import { idMap, idSet } from '../../operators/idMap.operator';
import { LocLabel } from '../clickable-row-table/models/data-table-config.models';
import { ResUsrVM } from './resident-user-select-view-model';
import { SubscribeComponent } from '../../classes/subscribe-component';

@Component({
  selector: 'app-resident-user-select',
  templateUrl: './resident-user-select.component.html',
  styleUrls: ['./resident-user-select.component.scss']
})
export class ResidentUserSelectComponent extends SubscribeComponent {
  private readonly NONE_OPT: ResUsrVM = { isAdmin: false, searchTags: '' };

  @Input() labelKey?: LocLabel | string | null = null;
  @Input() disabled = false;
  @Input() none = false;
  @Input() admins = false;

  @Output() selected = new EventEmitter<ResUsrVM>();

  public form = this.fb.control(undefined);
  public options: SelectInputData<ResUsrVM>[] = [];

  constructor(
    private fb: FormBuilder,
    private residentService: ResidentService,
    private estateService: EstateService,
    private ctxDS: ContextDataService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.none) this.form.patchValue(this.NONE_OPT);
    else this.form.addValidators(Validators.required);

    this.subscription$.add(this.form.valueChanges.subscribe((x) => this.selected.emit(x)));

    this.subscription$.add(
      combineLatest([
        this.residentService.getResidents$(),
        this.ctxDS.usersInEstate$.pipe(idMap()),
        this.estateService.getAdmins$().pipe(idSet('userId'))
      ]).subscribe(([residents, users, admins]) => {
        const vm: ResUsrVM = this.form.value;

        let opt: ResUsrVM[] = residents.map((resData) => {
          const userData = resData.userId ? users.get(resData.userId) : undefined;
          const resName = ToString.fullName(resData);
          const userName = ToString.fullName(userData) || undefined;
          return {
            resId: resData.id,
            resName,
            resData,
            userId: userData?.id,
            userData,
            userName,
            searchTags: userName ? `${resName} ${userName}` : resName,
            isAdmin: userData ? admins.delete(userData.id) : false
          };
        });

        if (this.admins) {
          admins.forEach((userId) => {
            const userData = users.get(userId);
            const userName = ToString.fullName(userData) || userId;
            opt.push({ userId, userName, userData, searchTags: userName, isAdmin: true });
          });
        }

        opt = opt.sort((a, b) => this.compare(a, b));

        this.options = SelectInputHelper.fromObjects(this.none ? [this.NONE_OPT, ...opt] : opt, 'searchTags', (x) => x);

        if (vm) this.patch(vm.resId, vm.userId);
      })
    );
  }

  public patch(resId: Nullable<string>, userId: Nullable<string>): void {
    let vm: ResUsrVM = resId ? { resId, userId: userId ?? undefined, isAdmin: false } : this.NONE_OPT;

    if (this.options.length) {
      let opt: ResUsrVM | undefined;
      if (resId) opt = this.options.find((x) => x.value.resId == resId)?.value;
      if (!opt && userId) opt = this.options.find((x) => x.value.userId == userId)?.value;

      if (opt) vm = opt;
      else if (this.none) vm = this.NONE_OPT;
    }

    this.form.patchValue(vm, { emitEvent: false });
    this.selected.emit(vm);
  }

  private compare(a: ResUsrVM, b: ResUsrVM): number {
    const aKey = a.resName || a.userName || '';
    const bKey = b.resName || b.userName || '';
    return aKey.localeCompare(bKey);
  }
}
